/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `IconPhoneProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface IconPhoneProps {
    /**
     * A string representing the CSS class to be applied to the IconPhoneIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A number or string representing the height of the IconPhoneIcon element.
     *
     * @default 32
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'IconPhone'
     */
    testId?: string;
    /**
     * A number or string representing the height of the IconPhoneIcon element.
     *
     * @default 32
     */
    width?: number | string;
}

/**
 * The `IconPhone` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `IconPhoneProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the IconPhoneIcon element to override its default styling.
 * @param props.height    A number or string representing the height of the IconPhoneIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the IconPhoneIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const IconPhoneComponent = <IconPhone className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const IconPhone = ({className = '', height = 32, ref, testId = 'IconPhone', width = 32}: IconPhoneProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 32 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M13.22 8.06c-.39-4.39-4.97-6.55-5.16-6.63a1 1 0 0 0-.59-.08C2.18 2.23 1.4 5.3 1.35 5.43a1 1 0 0 0 .02.54C7.7 25.56 20.8 29.2 25.12 30.38l.81.24q.15.04.3.04.23 0 .42-.09c.13-.06 3.25-1.52 4.01-6.31a1 1 0 0 0-.1-.61c-.06-.14-1.7-3.24-6.21-4.33a1 1 0 0 0-.89.2c-1.42 1.22-3.4 2.52-4.24 2.66-5.7-2.79-8.88-8.14-9-9.15-.07-.57 1.23-2.57 2.74-4.2a1 1 0 0 0 .26-.77" fill="currentColor" />
    </svg>
);

IconPhone.displayName = 'IconPhone';